import { findReferenceMatches, type ReferenceIndex } from './reference-index';

// Minimal hast shape — enough to walk and splice without pulling in unist utils.
type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

const SKIPPED_TAGS = new Set(['code', 'pre', 'a']);

// Rehype plugin that wraps known references in chip-styled spans so read-only
// previews (ProsePreview → MarkdownText) render them like the editor does.
const rehypeReferenceChips = (refs: ReferenceIndex) => () => (tree: HastNode) => {
  const visit = (node: HastNode) => {
    if (node.type === 'element' && SKIPPED_TAGS.has(node.tagName ?? '')) return;
    if (!node.children) return;
    const next: HastNode[] = [];
    for (const child of node.children) {
      if (child.type !== 'text' || !child.value) {
        visit(child);
        next.push(child);
        continue;
      }
      const matches = findReferenceMatches(child.value, refs);
      if (matches.length === 0) {
        next.push(child);
        continue;
      }
      let cursor = 0;
      for (const match of matches) {
        if (match.start > cursor) next.push({ type: 'text', value: child.value.slice(cursor, match.start) });
        next.push({
          type: 'element',
          tagName: 'span',
          properties: { className: ['prompt-ref', `prompt-ref-${match.kind}`] },
          children: [{ type: 'text', value: child.value.slice(match.start, match.end) }],
        });
        cursor = match.end;
      }
      if (cursor < child.value.length) next.push({ type: 'text', value: child.value.slice(cursor) });
    }
    node.children = next;
  };
  visit(tree);
};

export default rehypeReferenceChips;
